import { useMemo, useState } from 'react';
import {
  aantalBezoeken,
  afkoppelStatus,
  totaleBergingM3,
  type Registratie,
} from '@/types/domain';
import { isHuurwoning } from '@/data/huurwoningen';
import { adresRegel, adresSorteerSleutel, datumNL, getalNL } from '@/utils/format';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useIsTablet } from '@/hooks/useMediaQuery';
import './dashboard.css';

type SorteerVeld = 'adres' | 'datum' | 'status' | 'coach' | 'berging' | 'contact';
type Richting = 'op' | 'af';

interface RegistratieTabelProps {
  registraties: Registratie[];
  bijSelectie: (registratie: Registratie) => void;
}

const KOLOMMEN: { veld: SorteerVeld; kop: string }[] = [
  { veld: 'adres', kop: 'Adres' },
  { veld: 'status', kop: 'Status' },
  { veld: 'datum', kop: 'Laatste contact' },
  { veld: 'contact', kop: 'Contact' },
  { veld: 'coach', kop: 'Coach' },
  { veld: 'berging', kop: 'Berging m³' },
];

function sorteerWaarde(registratie: Registratie, veld: SorteerVeld): string | number {
  switch (veld) {
    case 'adres':
      return adresSorteerSleutel(registratie.adres);
    case 'datum':
      return registratie.datumBezoek;
    case 'status':
      return afkoppelStatus(registratie, isHuurwoning(registratie.adres));
    case 'coach':
      return registratie.coach.toLowerCase();
    case 'berging':
      return totaleBergingM3(registratie);
    case 'contact':
      return registratie.contactmomenten.length;
  }
}

/**
 * Tabel met alle registraties. Op smalle schermen wordt dit een lijst met
 * kaarten, omdat een horizontaal scrollende tabel op een telefoon onwerkbaar is.
 */
export function RegistratieTabel({
  registraties,
  bijSelectie,
}: RegistratieTabelProps): JSX.Element {
  const [sorteerVeld, setSorteerVeld] = useState<SorteerVeld>('datum');
  const [richting, setRichting] = useState<Richting>('af');
  const toonTabel = useIsTablet();

  const gesorteerd = useMemo(() => {
    const kopie = [...registraties];
    kopie.sort((a, b) => {
      const waardeA = sorteerWaarde(a, sorteerVeld);
      const waardeB = sorteerWaarde(b, sorteerVeld);
      const vergelijking =
        typeof waardeA === 'number' && typeof waardeB === 'number'
          ? waardeA - waardeB
          : String(waardeA).localeCompare(String(waardeB), 'nl');
      return richting === 'op' ? vergelijking : -vergelijking;
    });
    return kopie;
  }, [registraties, sorteerVeld, richting]);

  const schakelSortering = (veld: SorteerVeld) => {
    if (veld === sorteerVeld) {
      setRichting((huidig) => (huidig === 'op' ? 'af' : 'op'));
      return;
    }
    setSorteerVeld(veld);
    setRichting('op');
  };

  if (registraties.length === 0) {
    return <div className="tabel__leeg">Geen registraties gevonden met deze filters.</div>;
  }

  if (!toonTabel) {
    return (
      <div className="registratielijst">
        {gesorteerd.map((registratie) => (
          <button
            key={registratie.id}
            type="button"
            className="registratiekaart"
            onClick={() => bijSelectie(registratie)}
          >
            <div className="registratiekaart__kop">
              <span className="registratiekaart__adres">{adresRegel(registratie.adres)}</span>
              <StatusBadge status={afkoppelStatus(registratie, isHuurwoning(registratie.adres))} />
            </div>
            <div className="registratiekaart__meta">
              <span>{datumNL(registratie.datumBezoek)}</span>
              <span>{registratie.coach || 'Geen coach'}</span>
              {totaleBergingM3(registratie) > 0 && (
                <span>{getalNL(totaleBergingM3(registratie), 1)} m³</span>
              )}
              {registratie.fotos.length > 0 && <span>{registratie.fotos.length} foto’s</span>}
            </div>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="tabelhouder">
      <table className="tabel">
        <thead>
          <tr>
            {KOLOMMEN.map((kolom) => (
              <th
                key={kolom.veld}
                onClick={() => schakelSortering(kolom.veld)}
                aria-sort={
                  sorteerVeld === kolom.veld
                    ? richting === 'op'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {kolom.kop}
                {sorteerVeld === kolom.veld && (
                  <span className="tabel__sorteer">{richting === 'op' ? '▲' : '▼'}</span>
                )}
              </th>
            ))}
            {/* Administratieve kolommen. Ze vallen weg op een tablet in
                portret, anders past de tabel daar niet en moet je hem
                zijwaarts schuiven — met een vinger, binnen een pagina die
                zelf al omhoog en omlaag gaat. */}
            <th className="tabel__secundair">Eigendom</th>
            <th className="tabel__secundair">Subsidie</th>
            <th className="tabel__secundair">Realisatie</th>
            <th className="tabel__secundair">Foto’s</th>
          </tr>
        </thead>
        <tbody>
          {gesorteerd.map((registratie) => (
            <tr key={registratie.id} onClick={() => bijSelectie(registratie)}>
              <td className="tabel__adres">
                {adresRegel(registratie.adres)}
                <div style={{ fontWeight: 400, color: 'var(--kleur-tekst-secundair)' }}>
                  {registratie.adres.postcode} {registratie.adres.plaats}
                </div>
              </td>
              <td>
                <StatusBadge status={afkoppelStatus(registratie, isHuurwoning(registratie.adres))} />
              </td>
              <td>{datumNL(registratie.datumBezoek)}</td>
              <td className="tabel__getal">
                {registratie.contactmomenten.length > 1 ? (
                  <span
                    className="tabel__bijgewerkt"
                    title={`${registratie.contactmomenten.length} contactmomenten, waarvan ${aantalBezoeken(
                      registratie
                    )} aan de deur`}
                  >
                    {registratie.contactmomenten.length}×
                  </span>
                ) : (
                  '1×'
                )}
              </td>
              <td>{registratie.coach}</td>
              <td className="tabel__getal">{getalNL(totaleBergingM3(registratie), 1)}</td>
              <td className="tabel__secundair">{registratie.eigendom || '—'}</td>
              <td className="tabel__secundair">{registratie.subsidieStatus || '—'}</td>
              <td className="tabel__secundair">{registratie.realisatieStatus || '—'}</td>
              <td className="tabel__secundair tabel__getal">
                {registratie.fotos.length || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
